import React, { useEffect, useState } from 'react';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import Requester from './Requester';
import moment from 'moment';

function listadoDesdeResumen(data) {
    if (!data || data.existe !== true) {
        return null;
    }
    if (data.listado === true) {
        return true;
    }
    if (data.listado === false) {
        return false;
    }
    return null;
}

/** Coincidencia en listas PLD según respuesta Q-Detect (no confundir con éxito HTTP). */
function enListasDesdeConsultaPld(consultaPld) {
    return !!(consultaPld && consultaPld.success === true);
}

function formatFechaConsulta(iso) {
    if (!iso) {
        return '—';
    }
    try {
        return new Date(iso).toLocaleString('es-MX', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    } catch (_) {
        return String(iso);
    }
}

/**
 * Diálogo PLD: siempre abre modal.
 * — Si ya existe registro en `persons`: muestra fecha de consulta, abrir PDF y actualizar (pipeline completo).
 * — Si no: consulta POST /qeq/buscar y permite guardar PDF como antes.
 */
/** renderTrigger opcional: sustituye el botón «PLD» por un control personalizado (p. ej. badge de riesgo). */
function PldBusquedaDialog({ nombreCompleto, rfc, renderTrigger, onListadoChange }) {
    const [isOpen, setIsOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    /**
     * Color del botón PLD según último registro en BD: null → gris (sin consulta o legado sin flag);
     * true → warning (en listado / QEQ exitoso); false → success (consulta sin éxito).
     */
    const [listadoBoton, setListadoBoton] = useState(null);
    /** null = aún no definido; true = hay fila en BD; false = flujo sin registro */
    const [tieneRegistroEnDb, setTieneRegistroEnDb] = useState(null);
    const [requestDateIso, setRequestDateIso] = useState(null);
    const [payload, setPayload] = useState(null);
    const [savingPdf, setSavingPdf] = useState(false);
    const [pdfFeedback, setPdfFeedback] = useState(null);
    const [refreshing, setRefreshing] = useState(false);
    const [openingPdf, setOpeningPdf] = useState(false);

    function openPdfInNewTab(blob) {
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank', 'noopener,noreferrer');
        window.setTimeout(() => window.URL.revokeObjectURL(url), 30000);
    }

    function applyListado(listado) {
        setListadoBoton(listado);
        if (typeof onListadoChange === 'function' && typeof listado === 'boolean') {
            onListadoChange(listado);
        }
    }

    useEffect(() => {
        let cancel = false;
        const nombre = typeof nombreCompleto === 'string' ? nombreCompleto.trim() : '';
        if (!nombre) {
            setListadoBoton(null);
            return undefined;
        }
        (async () => {
            try {
                const res = await Requester.get('pld/resumen', { params: { nombreCompleto: nombre } });
                if (!cancel) {
                    setListadoBoton(listadoDesdeResumen(res && res.data));
                }
            } catch (_) {
                if (!cancel) {
                    setListadoBoton(null);
                }
            }
        })();
        return () => {
            cancel = true;
        };
    }, [nombreCompleto]);

    function resetModalState() {
        setLoading(false);
        setTieneRegistroEnDb(null);
        setRequestDateIso(null);
        setPayload(null);
        setPdfFeedback(null);
        setRefreshing(false);
        setOpeningPdf(false);
        setSavingPdf(false);
    }

    function toggle() {
        setIsOpen((o) => {
            if (o) {
                resetModalState();
            }
            return !o;
        });
    }

    async function handleGuardarPdf() {
        if (payload == null || loading) {
            return;
        }
        setPdfFeedback(null);
        setSavingPdf(true);
        try {
            const enListas = enListasDesdeConsultaPld(payload.data);
            const body = {
                nombreCompleto,
                rfc,
                horaGeneracion: moment().format('DD/MM/YYYY HH:mm:ss'),
                consultaPld: payload.data,
                consultaExitosa: enListas,
            };
            const res = await Requester.post('pld', body);
            const file = res && res.data && res.data.pldFile;
            if (res && res.data && typeof res.data.listado === 'boolean') {
                applyListado(res.data.listado);
            } else {
                applyListado(enListas);
            }
            setPdfFeedback({
                ok: true,
                text: file ? `PDF guardado en servidor (${file}).` : 'PDF guardado en servidor.',
            });
        } catch (err) {
            const msg =
                (err.response && err.response.data && err.response.data.error) ||
                err.message ||
                'No se pudo guardar el PDF.';
            setPdfFeedback({ ok: false, text: msg });
        } finally {
            setSavingPdf(false);
        }
    }

    async function cargarContenidoModal() {
        setLoading(true);
        setTieneRegistroEnDb(null);
        setRequestDateIso(null);
        setPayload(null);
        setPdfFeedback(null);
        try {
            const resumenRes = await Requester.get('pld/resumen', {
                params: { nombreCompleto },
            });
            const data = resumenRes && resumenRes.data;
            if (data && data.existe === true) {
                setTieneRegistroEnDb(true);
                setRequestDateIso(data.requestDate || null);
                applyListado(listadoDesdeResumen(data));
                setLoading(false);
                return;
            }
        } catch (_) {
            // Sin resumen tratable: seguimos como si no hubiera registro (consulta QEQ en vivo).
        }

        setTieneRegistroEnDb(false);
        try {
            const res = await Requester.post('qeq/buscar', { nombreCompleto, rfc });
            setPayload({ ok: true, data: res.data });
            applyListado(enListasDesdeConsultaPld(res.data));
        } catch (err) {
            const errData = err.response && err.response.data;
            setPayload({
                ok: false,
                data: errData != null ? errData : { status: err.message || 'Error de red' },
                httpStatus: err.response && err.response.status,
            });
        } finally {
            setLoading(false);
        }
    }

    async function handleClickPld(e) {
        e.preventDefault();
        e.stopPropagation();
        setIsOpen(true);
        await cargarContenidoModal();
    }

    async function handleOpenStoredPdf() {
        setOpeningPdf(true);
        setPdfFeedback(null);
        try {
            const pdfResponse = await Requester.get('pld', {
                params: { nombreCompleto, rfc },
                responseType: 'blob',
                validateStatus: (status) => status === 200 || status === 404,
            });
            if (pdfResponse.status === 200) {
                openPdfInNewTab(pdfResponse.data);
                return;
            }
            setPdfFeedback({
                ok: false,
                text: 'No se encontró el PDF almacenado para este nombre.',
            });
        } catch (err) {
            const msg =
                (err.response && err.response.data && err.response.data.error) ||
                err.message ||
                'No se pudo abrir el PDF.';
            setPdfFeedback({ ok: false, text: String(msg) });
        } finally {
            setOpeningPdf(false);
        }
    }

    async function handleActualizarBusqueda() {
        setRefreshing(true);
        setPdfFeedback(null);
        try {
            await Requester.post('pld/consulta-y-guardar', { nombreCompleto, rfc, force: true, horaGeneracion: moment().format('DD/MM/YYYY HH:mm:ss')});
            const r = await Requester.get('pld/resumen', { params: { nombreCompleto } });
            const data = r && r.data;
            if (data && data.existe === true) {
                setRequestDateIso(data.requestDate || null);
                applyListado(listadoDesdeResumen(data));
            }
            setPdfFeedback({
                ok: true,
                text: 'Consulta actualizada; PDF y registro guardados en el servidor.',
            });
        } catch (err) {
            const msg =
                (err.response && err.response.data && (err.response.data.error || err.response.data.status)) ||
                err.message ||
                'No se pudo actualizar la consulta PLD.';
            setPdfFeedback({ ok: false, text: String(msg) });
        } finally {
            setRefreshing(false);
        }
    }

    const bodyText =
        payload != null
            ? JSON.stringify(payload.data, null, 2)
            : '';

    const colorBotonPld =
        listadoBoton === true ? 'warning' : listadoBoton === false ? 'success' : 'secondary';

    const triggerProps = { onClick: handleClickPld, listadoBoton };

    return (
        <React.Fragment>
            {typeof renderTrigger === 'function' ? (
                renderTrigger(triggerProps)
            ) : (
                <Button type="button" color={colorBotonPld} size="sm" onClick={handleClickPld}>
                    PLD
                </Button>
            )}
            <Modal isOpen={isOpen} toggle={toggle} size="lg">
                <ModalHeader toggle={toggle}>
                    Consulta PLD — {nombreCompleto}{rfc ? ` - RFC: ${rfc}` : ''}
                </ModalHeader>
                <ModalBody>
                    {loading ? (
                        <p className="text-muted mb-0">Cargando…</p>
                    ) : tieneRegistroEnDb === true ? (
                        <div>
                            <p className="mb-3">
                                <strong>Fecha de consulta PLD:</strong>{' '}
                                {formatFechaConsulta(requestDateIso)}
                            </p>
                            <div
                                className="d-flex flex-wrap align-items-center"
                                style={{ columnGap: '1.25rem', rowGap: '0.5rem' }}
                            >
                                <Button
                                    type="button"
                                    color="primary"
                                    outline
                                    className="mb-0"
                                    disabled={openingPdf || refreshing}
                                    onClick={handleOpenStoredPdf}
                                >
                                    {openingPdf ? 'Abriendo…' : 'Abrir PDF'}
                                </Button>
                                <Button
                                    type="button"
                                    color="secondary"
                                    className="mb-0"
                                    disabled={openingPdf || refreshing}
                                    onClick={handleActualizarBusqueda}
                                >
                                    {refreshing ? 'Actualizando…' : 'Actualizar búsqueda'}
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <React.Fragment>
                            {payload && !payload.ok && payload.httpStatus ? (
                                <p className="text-danger small mb-2">
                                    Respuesta HTTP {payload.httpStatus}
                                </p>
                            ) : null}
                            {!loading && payload != null ? (
                                <pre
                                    className="bg-light border rounded p-2 mb-0"
                                    style={{
                                        maxHeight: '60vh',
                                        overflow: 'auto',
                                        fontSize: '12px',
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word',
                                    }}
                                >
                                    {bodyText}
                                </pre>
                            ) : !loading && tieneRegistroEnDb === false && payload == null ? (
                                <p className="text-muted small mb-0">Sin resultados de consulta.</p>
                            ) : null}
                        </React.Fragment>
                    )}
                    {pdfFeedback ? (
                        <p
                            className={
                                pdfFeedback.ok ? 'text-success small mb-0 mt-2' : 'text-danger small mb-0 mt-2'
                            }
                        >
                            {pdfFeedback.text}
                        </p>
                    ) : null}
                </ModalBody>
                <ModalFooter className="justify-content-end">
                    {tieneRegistroEnDb === false ? (
                        <Button
                            color="primary"
                            type="button"
                            outline
                            disabled={loading || payload == null || savingPdf}
                            onClick={handleGuardarPdf}
                        >
                            {savingPdf ? 'Guardando…' : 'Guardar en PDF'}
                        </Button>
                    ) : null}
                    <Button color="secondary" type="button" onClick={toggle}>
                        Cerrar
                    </Button>
                </ModalFooter>
            </Modal>
        </React.Fragment>
    );
}

export default PldBusquedaDialog;
